import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { getTeacherWorkshops } from '@/lib/db'
import { supabase } from '@/lib/supabase'
import type { UnitWorkshop } from '@/types'
import {
  Video,
  Calendar,
  Clock,
  Users,
  Search,
  Filter,
  ClipboardCheck,
  Loader2,
  AlertTriangle,
  X,
  CheckCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'

type WorkshopStatus = 'all' | 'upcoming' | 'completed'

function openMeetingUrl(rawUrl: string | null | undefined) {
  const trimmed = rawUrl?.trim() || ''
  if (!trimmed) {
    alert('No hay un enlace configurado para este taller.')
    return
  }
  const normalized = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, '')}`
  const win = window.open(normalized, '_blank', 'noopener,noreferrer')
  if (!win) {
    alert(`Tu navegador bloqueó la ventana emergente. Abre el taller manualmente:\n\n${normalized}`)
  }
}

export default function MyWorkshops() {
  const { teacher } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<WorkshopStatus>('all')
  const [workshops, setWorkshops] = useState<UnitWorkshop[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedWorkshop, setSelectedWorkshop] = useState<UnitWorkshop | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showNotAvailableModal, setShowNotAvailableModal] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)

  useEffect(() => {
    if (!teacher?.id) return

    async function loadWorkshops() {
      setLoading(true)
      const { data } = await getTeacherWorkshops(teacher!.id)
      if (data) setWorkshops(data)
      setLoading(false)
    }

    loadWorkshops()
  }, [teacher?.id])

  const today = new Date().toISOString().split('T')[0]

  const getWorkshopStatus = (w: UnitWorkshop): 'upcoming' | 'completed' => {
    return w.scheduled_date && w.scheduled_date >= today ? 'upcoming' : 'completed'
  }

  const filteredWorkshops = workshops.filter(workshop => {
    const title = workshop.title_es || workshop.title
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase())
    const status = getWorkshopStatus(workshop)
    const matchesStatus = statusFilter === 'all' || status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (workshop: UnitWorkshop) => {
    const status = getWorkshopStatus(workshop)
    const styles = {
      upcoming: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700'
    }
    const labels = {
      upcoming: 'Próximo',
      completed: 'Completado'
    }
    return (
      <span className={cn('px-2 py-1 rounded-full text-xs font-medium', styles[status])}>
        {labels[status]}
      </span>
    )
  }

  const isWorkshopAvailable = (workshop: UnitWorkshop) => {
    const now = new Date()
    const workshopDate = new Date(workshop.scheduled_date)
    const tenMinutesBefore = new Date(workshopDate.getTime() - 10 * 60 * 1000)
    const thirtyMinAfter = new Date(workshopDate.getTime() + 30 * 60 * 1000)
    return now >= tenMinutesBefore && now <= thirtyMinAfter
  }

  const formatWorkshopDateTime = (workshop: UnitWorkshop) => {
    const date = new Date(workshop.scheduled_date)
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleWorkshopClick = (workshop: UnitWorkshop) => {
    setSelectedWorkshop(workshop)
    if (isWorkshopAvailable(workshop)) {
      setShowConfirmModal(true)
    } else {
      setShowNotAvailableModal(true)
    }
  }

  const handleConfirmAttendance = async () => {
    if (!selectedWorkshop || !teacher) return
    setIsRegistering(true)
    try {
      const { data: existing } = await (supabase
        .from('teacher_workshop_attendance') as any)
        .select('id')
        .eq('workshop_id', selectedWorkshop.id)
        .eq('teacher_id', teacher.id)
        .maybeSingle()

      if (existing) {
        await (supabase.from('teacher_workshop_attendance') as any)
          .update({ status: 'attended', attended_at: new Date().toISOString() })
          .eq('id', existing.id)
      } else {
        const { error } = await (supabase.from('teacher_workshop_attendance') as any)
          .insert({
            workshop_id: selectedWorkshop.id,
            teacher_id: teacher.id,
            status: 'attended',
            attended_at: new Date().toISOString()
          })

        if (error) {
          console.error('Error registrando asistencia:', error)
          if (error.code === '42P01') {
            alert('La tabla de asistencia no existe. Contacta al administrador.')
            setIsRegistering(false)
            return
          }
        }
      }

      openMeetingUrl(selectedWorkshop.meeting_url)
      setShowConfirmModal(false)
    } catch (error) {
      console.error('Error:', error)
      alert('Hubo un error al registrar la asistencia.')
    } finally {
      setIsRegistering(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Mis Talleres</h1>
        <p className="text-muted-foreground">
          Gestiona tus talleres y sesiones en vivo
        </p>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as WorkshopStatus)}
            className="px-3 py-2 border rounded-lg bg-background"
          >
            <option value="all">Todos</option>
            <option value="upcoming">Próximos</option>
            <option value="completed">Completados</option>
          </select>
        </div>
      </div>

      {/* Lista de talleres */}
      <div className="grid gap-4">
        {filteredWorkshops.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border">
            <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium">
              No se encontraron talleres
            </p>
            <p className="text-muted-foreground">
              Los talleres asignados aparecerán aquí
            </p>
          </div>
        ) : (
          filteredWorkshops.map((workshop) => {
            const title = workshop.title_es || workshop.title
            const description = workshop.description_es || workshop.description
            const attendanceCount = (workshop as any).attendance?.length || 0
            const cohortName = (workshop as any).cohort?.name || null

            return (
              <div
                key={workshop.id}
                className="bg-card rounded-xl border p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Video className="w-8 h-8 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold">{title}</h3>
                        {cohortName && (
                          <p className="text-xs text-indigo-600 font-medium mt-0.5">
                            {cohortName}
                          </p>
                        )}
                        {description && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {description}
                          </p>
                        )}
                      </div>
                      {getStatusBadge(workshop)}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                      {workshop.scheduled_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{workshop.scheduled_date}</span>
                        </div>
                      )}
                      {workshop.scheduled_date && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{workshop.scheduled_date.slice(11, 16)}{workshop.duration_minutes ? ` (${workshop.duration_minutes} min)` : ''}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{attendanceCount}/{workshop.max_participants || '∞'}</span>
                      </div>
                      {(workshop as any).unit?.title && (
                        <span className="px-2 py-0.5 bg-muted rounded text-xs">
                          {(workshop as any).unit.title}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {(() => {
                      const scheduledDate = workshop.scheduled_date ? new Date(workshop.scheduled_date) : null
                      const thirtyMinAfter = scheduledDate ? new Date(scheduledDate.getTime() + 30 * 60 * 1000) : null
                      const now = new Date()
                      const isPastEntry = thirtyMinAfter ? now > thirtyMinAfter : false
                      const wsCompleted = getWorkshopStatus(workshop) === 'completed'

                      if (!wsCompleted && !isPastEntry) {
                        return (
                          <button
                            onClick={() => handleWorkshopClick(workshop)}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                          >
                            <Video className="w-4 h-4" />
                            Entrar al taller
                          </button>
                        )
                      }
                      if (isPastEntry && !wsCompleted) {
                        return (
                          <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-500 rounded-lg text-sm font-medium cursor-not-allowed">
                            <CheckCircle className="w-4 h-4" />
                            Taller ya realizado
                          </div>
                        )
                      }
                      return null
                    })()}
                    <Link
                      to={`/teacher/workshop/${workshop.id}/attendance`}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium"
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      Asistencia
                    </Link>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Modal: Taller no disponible */}
      {showNotAvailableModal && selectedWorkshop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-yellow-100">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Taller no disponible</h3>
              </div>
              <button
                onClick={() => setShowNotAvailableModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                El link de acceso al taller solo estara disponible <strong>10 minutos antes</strong> del horario programado.
              </p>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-800 font-medium">Fecha y hora del taller:</p>
                <p className="text-purple-900 font-semibold mt-1">{formatWorkshopDateTime(selectedWorkshop)}</p>
              </div>
            </div>
            <button
              onClick={() => setShowNotAvailableModal(false)}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Modal: Confirmar asistencia */}
      {showConfirmModal && selectedWorkshop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <Video className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Confirmar Asistencia
              </h2>
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-6">
                <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                <p className="text-lg font-semibold text-gray-900 mb-2">
                  IMPORTANTE
                </p>
                <p className="text-gray-700">
                  Si usted avanza al siguiente paso, se registrará su asistencia como <strong>"ASISTIDO"</strong> al taller.
                </p>
                <p className="text-gray-700 mt-3 font-medium">
                  ¿Seguro que desea avanzar?
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                disabled={isRegistering}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmAttendance}
                disabled={isRegistering}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
              >
                {isRegistering ? 'Registrando...' : 'Si, Ir al Taller'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
